/**
 * Row-label fallbacks for permissions Label Services has no `permission.resource.<name>`
 * entry for (TA4197567 items 6, 8, 9).
 *
 * Pure and `Locale`-free on purpose: the resolved label strings are passed in by the caller. That
 * keeps these unit tests independent of `labels.json`, and keeps one place — `FeatureAccessTab` —
 * responsible for reading copy.
 */

/**
 * The department prefix, mirroring `DEPARTMENT_PREFIX` in the lambda's `feature-group-map.ts`.
 * The trailing dot is required: live CC9 also returns a bare `"Website.Departments"` container
 * code, which is not a department and must not be named as one.
 */
const DEPARTMENT_PREFIX = "Website.Departments.";

/** The lambda's group id for department rows — the gate on applying the department template. */
export const DEPARTMENT_GROUP_ID = "DEPARTMENT";

const TEMPLATE_TOKEN = /\{\{label\}\}/g;

/**
 * `"Website.Departments.WHOLESALE_FLEET"` → `"Wholesale Fleet"`.
 *
 * Underscores become spaces and **every** word is capitalized, not just the first, so a custom
 * department reads consistently beside the ones Label Services does have a label for
 * ("Wholesale Parts Department", "Body Shop Department").
 *
 * Returns `null` when `code` is not the prefix plus a non-empty type — which excludes the bare
 * `"Website.Departments"` container code.
 */
export const departmentTypeToName = (code: string): string | null => {
  if (!code.startsWith(DEPARTMENT_PREFIX)) return null;

  const words = code
    .slice(DEPARTMENT_PREFIX.length)
    .split("_")
    .filter(word => word.trim().length > 0);

  if (words.length === 0) return null;

  return words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Fills the `CUSTOM_WEBSITE_DEPARTMENT` template — `"{{label}} Department - control the
 * {{label}} section of your website"`. Every occurrence is substituted, not just the first,
 * because the registered copy uses the token twice.
 */
export const applyDepartmentTemplate = (
  template: string,
  name: string
): string => template.replace(TEMPLATE_TOKEN, name);
